import {
  getAntiForgeryTokenFromCookieOrMeta,
  httpClient,
  primeAbpAntiforgerySession,
} from '@/shared/api';
import type {
  AbpApplicationConfigurationDto,
  IdentityUserDto,
  LoginRequestDto,
  LoginResponseDto,
  ProfileResponseDto,
  UserRolesResponseDto,
} from './auth.dto';

/**
 * Auth API path prefix (relative to apiConfig.baseUrl). Change here to match your ABP module route.
 * With baseUrl = '/api', full URL is /api/account/login etc.
 */
const AUTH_PREFIX = '/account';

/**
 * Identity API path prefix for user roles (relative to apiConfig.baseUrl).
 * Full URL: /api/identity/users/{id}/roles
 */
const IDENTITY_PREFIX = '/identity';

/** ABP application bootstrap (includes `currentUser` + `roles` without Identity Users permissions). */
const ABP_PREFIX = '/abp';

/** Builds headers for ABP logout (requires RequestVerificationToken and X-Requested-With). */
function getLogoutHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };
  const token = getAntiForgeryTokenFromCookieOrMeta();
  if (token) {
    headers['RequestVerificationToken'] = token;
    headers['X-XSRF-TOKEN'] = token;
  }
  return headers;
}

/** Fetches user by username (includes id for roles lookup). */
export const getUserByUsername = (userName: string) =>
  httpClient.get<IdentityUserDto>(
    `${IDENTITY_PREFIX}/users/by-username/${encodeURIComponent(userName)}`
  );

/** Fetches roles for a user. Use id from getUserByUsername(). */
export const getUserRoles = (userId: string) =>
  httpClient.get<UserRolesResponseDto>(
    `${IDENTITY_PREFIX}/users/${encodeURIComponent(userId)}/roles`
  );

export const authApi = {
  login: async (body: LoginRequestDto) => {
    /** Ensures .AspNetCore.Antiforgery cookie + request token exist before POST (ABP antiforgery filter). */
    await primeAbpAntiforgerySession();
    return httpClient.post<LoginResponseDto>(`${AUTH_PREFIX}/login`, body);
  },

  logout: () =>
    httpClient.get<void>(`${AUTH_PREFIX}/logout`, {
      headers: getLogoutHeaders(),
    }),

  getProfile: () =>
    httpClient.get<ProfileResponseDto>(`${AUTH_PREFIX}/my-profile`),

  /** Preferred for SPA session: works for users without Account / Identity user APIs. */
  getApplicationConfiguration: () =>
    httpClient.get<AbpApplicationConfigurationDto>(`${ABP_PREFIX}/application-configuration`),

  getUserByUsername,
  getUserRoles,
};
